import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { cn } from "../../utils/cn";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../supabase";
import NotificationCenter from "../NotificationCenter";
import LanguageSelector from "../LanguageSelector";
import { useLanguage } from "../../context/LanguageContext";

const navigation = [
  { name: "Home", href: "/" },
  { name: "Find Food", href: "/find-food" },
  { name: "Donate", href: "/donate" },
  { name: "Community", href: "/community" },
  { name: "Impact", href: "/impact" },
];

const moreNavigation = [
  { name: "About", href: "/about" },
  { name: "Help Center", href: "/help" },
  { name: "Partnership", href: "/partnership" },
  { name: "Success Stories", href: "/success-stories" },
];

// Custom translations for dropdown items
const getTranslatedText = (text: string, language: string): string => {
  const translations: Record<string, Record<string, string>> = {
    About: {
      si: "පිළිබඳ",
      ta: "பற்றி",
      en: "About",
    },
    "Help Center": {
      si: "උදව් මධ්‍යස්ථානය",
      ta: "உதவி மையம்",
      en: "Help Center",
    },
    Partnership: {
      si: "හවුල්කාරිත්වය",
      ta: "கூட்டாண்மை",
      en: "Partnership",
    },
    "Success Stories": {
      si: "සාර්ථක කතන්දර",
      ta: "வெற்றிக் கதைகள்",
      en: "Success Stories",
    },
    More: {
      si: "තව",
      ta: "மேலும்",
      en: "More",
    },
  };

  return translations[text]?.[language] || text;
};

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  // Ref on the button (not the menu itself) for fine placement
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { currentLanguage } = useLanguage();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userProfileImage, setUserProfileImage] = useState<string | null>(null);

  useEffect(() => {
    // Close menu on outside click
    if (!profileMenuOpen) return;
    function onClick(e: MouseEvent) {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(e.target as Node) &&
        profileButtonRef.current &&
        !profileButtonRef.current.contains(e.target as Node)
      ) {
        setProfileMenuOpen(false);
      }
    }
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [profileMenuOpen]);

  useEffect(() => {
    // Close more menu on outside click
    if (!moreMenuOpen) return;
    function onClick(e: MouseEvent) {
      if (
        moreMenuRef.current &&
        !moreMenuRef.current.contains(e.target as Node) &&
        moreButtonRef.current &&
        !moreButtonRef.current.contains(e.target as Node)
      ) {
        setMoreMenuOpen(false);
      }
    }
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [moreMenuOpen]);

  // (Dropdown position state removed as it was unused)

  const fetchUserRole = async () => {
    if (!user) return null;
    if (userRole) return userRole;
    try {
      const { data, error } = await supabase
        .from("users")
        .select("role, profile_image_url")
        .eq("id", user.id)
        .single();

      if (error) throw error;
      if (data) {
        const role = data.role;
        const profileImage = data.profile_image_url;
        setUserRole(role);
        setUserProfileImage(profileImage);
        return role;
      }
    } catch (e) {}
    return null;
  };

  const handleGoToDashboard = async () => {
    const role = await fetchUserRole();
    setProfileMenuOpen(false);
    if (role === "donor") navigate("/dashboard/donor");
    else if (role === "recipient") navigate("/dashboard/recipient");
    else if (role === "volunteer") navigate("/dashboard/volunteer");
    else navigate("/");
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setProfileMenuOpen(false);
    setUserRole(null);
    setUserProfileImage(null);
    navigate("/", { replace: true });
  };

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-2xl border-b border-white/30 shadow-2xl">
      <nav className="container-modern" aria-label="Top">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-4 group">
              <div className="relative">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 shadow-lg group-hover:shadow-xl group-hover:shadow-emerald-500/40 transform group-hover:scale-105 group-hover:rotate-2 transition-all duration-500">
                  <span className="text-sm font-bold text-white notranslate">
                    M
                  </span>
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full animate-pulse shadow-md"></div>
                <div className="absolute -bottom-0.5 -left-0.5 w-3 h-3 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full animate-bounce-gentle"></div>
              </div>
              <div className="flex flex-col notranslate">
                <span className="text-lg font-bold bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent group-hover:from-emerald-500 group-hover:to-cyan-500 transition-all duration-500">
                  MealBridge
                </span>
                <span className="text-xs font-bold text-amber-600 -mt-0.5 tracking-wider">
                  SRI LANKA
                </span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-3">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "relative px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-300 group",
                  location.pathname === item.href
                    ? "text-white bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 shadow-lg shadow-emerald-500/40 transform scale-105"
                    : "text-slate-600 hover:text-emerald-600 hover:bg-emerald-50/80 hover:shadow-md hover:shadow-emerald-500/20 transform hover:-translate-y-0.5 hover:scale-105"
                )}
              >
                {item.name}
                {location.pathname === item.href && (
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-2xl -z-10 blur-lg opacity-60"></div>
                )}
                <div className="absolute inset-x-0 -bottom-1 h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 rounded-full"></div>
              </Link>
            ))}

            {/* More Dropdown */}
            <div className="relative">
              <button
                ref={moreButtonRef}
                className={cn(
                  "relative px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-300 group flex items-center space-x-1",
                  moreMenuOpen ||
                    moreNavigation.some(
                      (item) => location.pathname === item.href
                    )
                    ? "text-white bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 shadow-lg shadow-emerald-500/40 transform scale-105"
                    : "text-slate-600 hover:text-emerald-600 hover:bg-emerald-50/80 hover:shadow-md hover:shadow-emerald-500/20 transform hover:-translate-y-0.5 hover:scale-105"
                )}
                onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                aria-haspopup="true"
                {...(moreMenuOpen
                  ? { "aria-expanded": "true" }
                  : { "aria-expanded": "false" })}
                aria-label="Open more menu"
                type="button"
              >
                <span>{getTranslatedText("More", currentLanguage)}</span>
                <ChevronDownIcon
                  className={cn(
                    "w-4 h-4 transition-transform duration-200",
                    moreMenuOpen ? "rotate-180" : ""
                  )}
                />
                {(moreMenuOpen ||
                  moreNavigation.some(
                    (item) => location.pathname === item.href
                  )) && (
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-2xl -z-10 blur-lg opacity-60"></div>
                )}
                <div className="absolute inset-x-0 -bottom-1 h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 rounded-full"></div>
              </button>

              {moreMenuOpen && (
                <div
                  ref={moreMenuRef}
                  className="absolute top-full left-0 mt-2 w-48 bg-white/95 backdrop-blur-2xl rounded-xl shadow-xl border border-white/30 p-2 animate-slide-down z-50"
                >
                  {moreNavigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={cn(
                        "flex items-center w-full px-4 py-2.5 text-left text-slate-700 hover:bg-emerald-50 rounded-lg transition-all duration-200 group",
                        location.pathname === item.href
                          ? "bg-emerald-50 text-emerald-700"
                          : ""
                      )}
                      onClick={() => setMoreMenuOpen(false)}
                    >
                      <span className="text-sm font-medium">
                        {getTranslatedText(item.name, currentLanguage)}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex md:items-center md:space-x-4">
            <LanguageSelector showLabel={false} />
            {!loading && user && <NotificationCenter />}

            {!loading && user ? (
              <div className="relative flex items-center space-x-3">
                <button
                  ref={profileButtonRef}
                  className="flex items-center space-x-3 px-4 py-2 bg-white/90 backdrop-blur-xl border-2 border-white/30 rounded-xl shadow-lg hover:shadow-xl hover:bg-white hover:border-emerald-200 transform hover:-translate-y-0.5 hover:scale-105 transition-all duration-300 group"
                  onClick={async () => {
                    setProfileMenuOpen((v) => !v);
                    if (!userRole) await fetchUserRole();
                  }}
                  aria-haspopup="true"
                  {...(profileMenuOpen
                    ? { "aria-expanded": "true" }
                    : { "aria-expanded": "false" })}
                  aria-label="Open user menu"
                  type="button"
                >
                  {userProfileImage || user.user_metadata?.avatar_url ? (
                    <img
                      src={userProfileImage || user.user_metadata.avatar_url}
                      alt={user.user_metadata?.full_name || "User"}
                      className="object-cover w-8 h-8 rounded-lg ring-2 ring-emerald-200 group-hover:ring-emerald-400 transition-all duration-300 shadow-md"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 text-white font-bold text-sm shadow-md group-hover:shadow-lg transition-all duration-300">
                      {(user.user_metadata?.full_name || user.email || "U")
                        .charAt(0)
                        .toUpperCase()}
                    </div>
                  )}
                  <div className="flex flex-col items-start">
                    <span className="text-xs font-semibold text-slate-900 group-hover:text-emerald-700 transition-colors duration-300">
                      {user.user_metadata?.full_name || user.email || "User"}
                    </span>
                    <span className="text-xs text-slate-500 capitalize group-hover:text-emerald-600 transition-colors duration-300">
                      {userRole || "Member"}
                    </span>
                  </div>
                </button>
                {profileMenuOpen && (
                  <div
                    ref={profileMenuRef}
                    className="absolute top-full right-0 mt-2 w-56 bg-white/95 backdrop-blur-2xl rounded-xl shadow-xl border border-white/30 p-3 animate-slide-down"
                  >
                    <button
                      className="flex items-center w-full px-4 py-2.5 text-left text-slate-700 hover:bg-emerald-50 rounded-lg transition-all duration-200 group"
                      onClick={() => {
                        setProfileMenuOpen(false);
                        navigate("/profile");
                      }}
                    >
                      <UserCircleIcon className="w-4 h-4 mr-3 text-emerald-500 group-hover:text-emerald-600 transition-colors duration-200" />
                      <span className="text-sm font-medium">View Profile</span>
                    </button>
                    <button
                      className="flex items-center w-full px-4 py-2.5 text-left text-slate-700 hover:bg-emerald-50 rounded-lg transition-all duration-200 group"
                      onClick={handleGoToDashboard}
                    >
                      <svg
                        className="w-4 h-4 mr-3 text-emerald-500 group-hover:text-emerald-600 transition-colors duration-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z"
                        />
                      </svg>
                      <span className="text-sm font-medium">
                        Go to Dashboard
                      </span>
                    </button>
                    <div className="border-t border-slate-200 my-1.5"></div>
                    <button
                      className="flex items-center w-full px-4 py-2.5 text-left text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 group"
                      onClick={handleSignOut}
                    >
                      <svg
                        className="w-4 h-4 mr-3 text-red-500 group-hover:text-red-600 transition-colors duration-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                      <span className="text-sm font-medium">Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              !loading && (
                <Link to="/auth" className="btn-gradient">
                  Sign In
                </Link>
              )
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              type="button"
              className="p-3 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-300 notranslate"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <Bars3Icon className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-2xl border-t border-white/30 shadow-2xl">
            <div className="px-6 py-8 space-y-6">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "flex items-center px-6 py-4 text-base font-bold rounded-2xl transition-all duration-500 transform hover:scale-105",
                    location.pathname === item.href
                      ? "text-white bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 shadow-2xl shadow-emerald-500/40"
                      : "text-slate-700 hover:text-emerald-600 hover:bg-emerald-50/80 hover:shadow-lg hover:shadow-emerald-500/20"
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}

              {/* More Pages in Mobile */}
              {moreNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "flex items-center px-6 py-4 text-base font-bold rounded-2xl transition-all duration-500 transform hover:scale-105",
                    location.pathname === item.href
                      ? "text-white bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 shadow-2xl shadow-emerald-500/40"
                      : "text-slate-700 hover:text-emerald-600 hover:bg-emerald-50/80 hover:shadow-lg hover:shadow-emerald-500/20"
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {getTranslatedText(item.name, currentLanguage)}
                </Link>
              ))}

              {/* Language Selector for Mobile */}
              <div className="px-4 py-2">
                <LanguageSelector showLabel={true} />
              </div>

              <div className="px-4 py-2">
                {!loading && user ? (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-primary-50 to-accent-50 rounded-lg border border-primary-200">
                      {userProfileImage || user.user_metadata?.avatar_url ? (
                        <img
                          src={
                            userProfileImage || user.user_metadata.avatar_url
                          }
                          alt={user.user_metadata?.full_name || "User"}
                          className="w-8 h-8 rounded-full ring-2 ring-primary-200"
                        />
                      ) : (
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 text-white font-bold text-sm">
                          {(user.user_metadata?.full_name || user.email || "U")
                            .charAt(0)
                            .toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-neutral-900">
                          {user.user_metadata?.full_name ||
                            user.email ||
                            "User"}
                        </p>
                        <p className="text-xs text-neutral-500 capitalize">
                          {userRole || "Member"}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <button
                        className="flex items-center w-full px-3 py-2 text-left text-neutral-700 hover:bg-primary-50 rounded-lg transition-colors duration-200 group"
                        onClick={() => {
                          setProfileMenuOpen(false);
                          setMobileMenuOpen(false);
                          navigate("/profile");
                        }}
                      >
                        <UserCircleIcon className="w-4 h-4 mr-3 text-primary-500 group-hover:text-primary-600" />
                        <span className="text-sm font-medium">
                          View Profile
                        </span>
                      </button>
                      <button
                        className="flex items-center w-full px-3 py-2 text-left text-neutral-700 hover:bg-primary-50 rounded-lg transition-colors duration-200 group"
                        onClick={() => {
                          handleGoToDashboard();
                          setMobileMenuOpen(false);
                        }}
                      >
                        <svg
                          className="w-4 h-4 mr-3 text-primary-500 group-hover:text-primary-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z"
                          />
                        </svg>
                        <span className="text-sm font-medium">
                          Go to Dashboard
                        </span>
                      </button>
                      <button
                        className="flex items-center w-full px-3 py-2 text-left text-error-600 hover:bg-error-50 rounded-lg transition-colors duration-200 group"
                        onClick={() => {
                          handleSignOut();
                          setMobileMenuOpen(false);
                        }}
                      >
                        <svg
                          className="w-4 h-4 mr-3 text-error-500 group-hover:text-error-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                          />
                        </svg>
                        <span className="text-sm font-medium">Sign Out</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  !loading && (
                    <Link
                      to="/auth"
                      className="block w-full text-center btn-gradient"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Sign In
                    </Link>
                  )
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
